// NoteEditModal.js - Note Text and Color Editing Modal

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import useStore from '../../store/useStore';
import THEME from '../../styles/theme';

const NoteEditModal = () => {
  const {
    isNoteModalOpen,
    editingNoteId,
    nodes,
    closeNoteModal,
    updateNodeData
  } = useStore();

  const [localText, setLocalText] = useState('');
  const [localColor, setLocalColor] = useState('yellow');

  // Draggable state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  const textareaRef = useRef(null);

  const editingNode = nodes.find(n => n.id === editingNoteId);

  useEffect(() => {
    if (editingNode && editingNode.data.parameters) {
      setLocalText(editingNode.data.parameters.text?.value || '');
      setLocalColor(editingNode.data.parameters.color?.value || 'yellow');
      // Auto-focus textarea when modal opens
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.select();
        }
      }, 100);
    }
  }, [editingNode]);

  // Reset position when modal opens/closes
  useEffect(() => {
    if (isNoteModalOpen) {
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [isNoteModalOpen]);

  // Mouse event handlers for dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !modalRef.current) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      const modal = modalRef.current;
      const rect = modal.getBoundingClientRect();

      // Calculate new position
      let newX = position.x + deltaX;
      let newY = position.y + deltaY;

      // Boundary constraints - ensure at least 100px of modal remains visible
      const minVisible = 100;
      const halfWidth = rect.width / 2;
      const halfHeight = rect.height / 2;

      const maxX = window.innerWidth / 2 - minVisible;
      const minX = -window.innerWidth / 2 + minVisible;
      const maxY = window.innerHeight / 2 - minVisible;
      const minY = -window.innerHeight / 2 + minVisible;

      newX = Math.max(minX, Math.min(newX, maxX));
      newY = Math.max(minY, Math.min(newY, maxY));

      setPosition({ x: newX, y: newY });
      setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, position]);

  if (!isNoteModalOpen || !editingNode) return null;

  const handleMouseDown = (e) => {
    // Only start drag if clicking on header (not close button)
    if (e.target.closest('button')) return;

    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleSave = () => {
    const updatedParams = {
      ...editingNode.data.parameters,
      text: {
        ...editingNode.data.parameters.text,
        value: localText
      },
      color: {
        ...editingNode.data.parameters.color,
        value: localColor
      }
    };

    updateNodeData(editingNoteId, { parameters: updatedParams });
    closeNoteModal();
  };

  const handleCancel = () => {
    closeNoteModal();
  };

  const handleKeyDown = (e) => {
    // Ctrl+Enter saves (regular Enter creates new lines)
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSave();
    }
    // Escape cancels
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const colorOptions = [
    { name: 'yellow', label: 'Yellow', color: THEME.annotationYellow },
    { name: 'orange', label: 'Orange', color: THEME.annotationOrange },
    { name: 'red', label: 'Red', color: THEME.annotationRed },
    { name: 'cyan', label: 'Cyan', color: THEME.annotationCyan }
  ];

  // Get the background color for the modal based on current note color
  const colorMap = {
    yellow: THEME.annotationYellow,
    orange: THEME.annotationOrange,
    red: THEME.annotationRed,
    cyan: THEME.annotationCyan
  };
  const modalBackgroundColor = colorMap[localColor] || THEME.annotationYellow;

  return (
    <Overlay onClick={handleCancel}>
      <Modal
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          backgroundColor: modalBackgroundColor,
        }}
        $isDragging={isDragging}
      >
        <ModalHeader onMouseDown={handleMouseDown}>
          <ModalTitle>Edit Note</ModalTitle>
          <CloseButton onClick={handleCancel}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <Section>
            <Label>Note Text</Label>
            <Textarea
              ref={textareaRef}
              value={localText}
              onChange={(e) => setLocalText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your note here..."
              rows={8}
            />
            <Hint>Press Ctrl+Enter to save, Escape to cancel</Hint>
          </Section>

          <Section>
            <Label>Color</Label>
            <ColorGrid>
              {colorOptions.map(option => (
                <ColorButton
                  key={option.name}
                  $color={option.color}
                  $isSelected={localColor === option.name}
                  onClick={() => setLocalColor(option.name)}
                  title={option.label}
                >
                  {localColor === option.name && <CheckMark>✓</CheckMark>}
                </ColorButton>
              ))}
            </ColorGrid>
          </Section>
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${THEME.zIndex.modal};
  animation: fadeIn 150ms ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  background: ${THEME.panelBackground};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  width: auto;
  min-width: 480px;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 250ms ease-out;
  transition: ${props => props.$isDragging ? 'none' : 'transform 0.1s ease-out'};
  user-select: ${props => props.$isDragging ? 'none' : 'auto'};
  overflow: hidden;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translate(-50%, -48%) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${THEME.spacing.lg};
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  cursor: move;
  user-select: none;
  position: relative;

  &:active {
    cursor: grabbing;
  }
`;

const ModalTitle = styled.h2`
  color: #333;
  font-size: ${THEME.fontSize.xl};
  margin: 0;
  text-align: center;
  font-weight: 600;
`;

const CloseButton = styled.button`
  position: absolute;
  right: ${THEME.spacing.lg};
  background: rgba(0, 0, 0, 0.08);
  color: #333;
  border: none;
  border-radius: 6px;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background ${THEME.transitionFast};

  &:hover {
    background: rgba(0, 0, 0, 0.15);
  }

  &:active {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const ModalBody = styled.div`
  padding: ${THEME.spacing.xl};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.xl};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.sm};
`;

const Label = styled.label`
  color: #333;
  font-size: ${THEME.fontSize.md};
  font-weight: 600;
`;

const Textarea = styled.textarea`
  background: rgba(255, 255, 255, 0.75);
  color: #333;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  padding: ${THEME.spacing.md};
  font-size: 18px;
  font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.6;
  resize: vertical;
  min-height: 150px;
  transition: all ${THEME.transitionFast};

  &:focus {
    outline: none;
    border-color: rgba(0, 0, 0, 0.25);
    background: rgba(255, 255, 255, 0.85);
  }

  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }
`;

const Hint = styled.div`
  color: rgba(0, 0, 0, 0.5);
  font-size: ${THEME.fontSize.xs};
  font-style: italic;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${THEME.spacing.sm};
`;

const ColorButton = styled.button`
  background: ${props => props.$color};
  border: 2px solid ${props => props.$isSelected ? '#333' : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 8px;
  height: 50px;
  cursor: pointer;
  transition: all ${THEME.transitionFast};
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
    border-color: ${props => props.$isSelected ? '#333' : 'rgba(0, 0, 0, 0.25)'};
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const CheckMark = styled.span`
  color: #333;
  font-size: 24px;
  font-weight: bold;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${THEME.spacing.md};
  padding: ${THEME.spacing.lg};
  border-top: 1px solid rgba(0, 0, 0, 0.12);
`;

const Button = styled.button`
  padding: ${THEME.spacing.sm} ${THEME.spacing.lg};
  border: none;
  border-radius: 6px;
  font-size: ${THEME.fontSize.md};
  font-weight: 500;
  cursor: pointer;
  transition: all ${THEME.transitionFast};

  ${props => props.variant === 'primary' && `
    background: ${THEME.buttonPrimary};
    color: white;

    &:hover {
      background: ${THEME.buttonHover};
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: rgba(0, 0, 0, 0.08);
    color: #333;

    &:hover {
      background: rgba(0, 0, 0, 0.12);
    }
  `}

  &:active {
    transform: scale(0.98);
  }
`;

export default NoteEditModal;
